import { parse } from "date-fns";

import { DAYS_OF_WEEK, PricingType, VisitType } from "./types";

/** The value shape of a `type="time"` input, and what a visit draft holds. */
const TIME_INPUT_FORMAT = "HH:mm";

/**
 * How home-health-api stores `estimatedStartTime` (see its `normalizeTimeFormat`).
 * It accepts a spaceless/lowercase variant on write and normalizes to this on
 * storage, so reads are always in this shape — but parse both to stay tolerant
 * of rows written before normalization landed.
 */
const ACCEPTED_API_TIME_FORMATS = ["h:mm a", "h:mma"];

const HOURS_PER_HALF_DAY = 12;

/** The two opt-ins, as a form holds them. */
export interface FlexibleScheduleData {
  hasFixedStartTime: boolean;
  /** `HH:mm`, or empty when no time has been picked yet. */
  estimatedStartTime: string;
  hasSpecificDaysOfWeek: boolean;
  daysOfWeek: string[];
}

export type FlexibleScheduleFieldErrors = Partial<
  Record<"estimatedStartTime" | "daysOfWeek", string>
>;

/**
 * Whether a visit can carry a fixed start time and specific weekdays. Hourly
 * REGULAR visits are flexible by default; these two are the opt-in escape
 * hatches, and no other visit shape offers them. Every gate on those controls —
 * form, validation, create/update payload, and display — resolves through here
 * so the desktop and mobile flows can't drift apart.
 */
export function hasFlexibleScheduleOptions(params: {
  visitType: VisitType | undefined;
  pricingType: PricingType | undefined;
}): boolean {
  return params.visitType === VisitType.REGULAR && params.pricingType === PricingType.PER_HOUR;
}

/**
 * Checking a box is itself the assertion that a value exists, so an empty value
 * behind a checked box is the error. Nothing is required while a box is
 * unchecked, and nothing is checked at all when the controls aren't offered —
 * a hidden control must never block a form.
 */
export function getFlexibleScheduleFieldErrors(params: {
  data: FlexibleScheduleData;
  visitType: VisitType | undefined;
  pricingType: PricingType | undefined;
}): FlexibleScheduleFieldErrors {
  const { data, visitType, pricingType } = params;
  const errors: FlexibleScheduleFieldErrors = {};

  if (!hasFlexibleScheduleOptions({ visitType, pricingType })) {
    return errors;
  }

  if (data.hasFixedStartTime && data.estimatedStartTime.trim() === "") {
    errors.estimatedStartTime = "Start time is required";
  }

  if (data.hasSpecificDaysOfWeek && data.daysOfWeek.length === 0) {
    errors.daysOfWeek = "Select at least one weekday";
  }

  return errors;
}

function weekdayIndex(day: string): number {
  const index = DAYS_OF_WEEK.indexOf(day);
  // Anything the API sends that isn't a known weekday sorts last rather than
  // jumping to the front on -1.
  return index === -1 ? DAYS_OF_WEEK.length : index;
}

/**
 * Weekdays arrive in whatever order they were clicked or stored, so put them in
 * week order (`DAYS_OF_WEEK` starts on Sunday) before showing them.
 */
export function sortWeekdays(days: string[]): string[] {
  return [...days].sort((a, b) => weekdayIndex(a) - weekdayIndex(b));
}

function parseWithFormats(value: string, formats: string[]): Date | undefined {
  if (value.trim() === "") {
    return undefined;
  }

  for (const dateFormat of formats) {
    const parsed = parse(value, dateFormat, new Date());
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return undefined;
}

/** Parse the `HH:mm` value a visit draft holds. */
export function parseTimeInputValue(value: string): Date | undefined {
  return parseWithFormats(value, [TIME_INPUT_FORMAT]);
}

/** Parse the `estimatedStartTime` string the API returns. */
export function parseApiStartTime(value: string): Date | undefined {
  return parseWithFormats(value, ACCEPTED_API_TIME_FORMATS);
}

// Both formatters are hand-rolled because @clipboard-health/date-time has no
// 24-hour or spaced-meridiem format, and date-fns' `format` is lint-restricted.

/** Render a Date back into the `HH:mm` a visit draft holds. */
export function toTimeInputValue(date: Date): string {
  return `${padTwoDigits(date.getHours())}:${padTwoDigits(date.getMinutes())}`;
}

/**
 * Display a start time the way the API stores it, so a draft in the wizard and
 * the posted visit it becomes read identically.
 */
export function formatVisitStartTime(date: Date): string {
  const hours = date.getHours();
  const hourOfHalfDay = hours % HOURS_PER_HALF_DAY;

  return [
    hourOfHalfDay === 0 ? HOURS_PER_HALF_DAY : hourOfHalfDay,
    ":",
    padTwoDigits(date.getMinutes()),
    hours < HOURS_PER_HALF_DAY ? " AM" : " PM",
  ].join("");
}

function padTwoDigits(value: number): string {
  return value.toString().padStart(2, "0");
}
